/**
 * The one export path (FR-RPT-02).
 *
 * Every report reaches every format through this function. Adding a report means adding
 * a builder in `core/reports.ts` and nothing here; adding a format means one `case`.
 * That is the requirement, expressed as the only route to a file.
 *
 * The heavy libraries load on demand (AD-06) — exceljs and pdfmake are imported inside
 * their own modules, so neither is fetched until the user actually picks that format.
 */

import { columnsFor, reportFilename, type Report } from '../../core/reports';
import { toCsv } from '../../core/exportCsv';
import type { Branding, Template } from './exportPdf';

export type ExportFormat = 'xlsx' | 'csv' | 'pdf';

export const EXPORT_FORMATS: { id: ExportFormat; label: string; note: string }[] = [
  { id: 'xlsx', label: 'Excel', note: 'Real dates and numbers that sum' },
  { id: 'csv', label: 'CSV', note: 'Plain text, opens anywhere' },
  { id: 'pdf', label: 'PDF', note: 'Branded, for sending on' },
];

export interface ExportOptions {
  report: Report;
  format: ExportFormat;
  template: Template;
  branding: Branding;
  /** Passed in rather than read here, so the output is a function of its inputs. */
  generatedOn: Date;
}

export async function buildExport(
  options: ExportOptions,
): Promise<{ blob: Blob; filename: string }> {
  const { report, format, template, branding, generatedOn } = options;

  // The template decides the columns for every format, not just PDF — otherwise a
  // "personal" Excel export would carry payee and reference columns the matching PDF
  // does not, and the two files would disagree about what the report is.
  const columns = columnsFor(report, template);

  switch (format) {
    case 'csv':
      return {
        blob: new Blob([toCsv(report, columns)], { type: 'text/csv;charset=utf-8' }),
        filename: reportFilename(report, 'csv'),
      };
    case 'xlsx': {
      const { reportToExcelBlob } = await import('./exportExcel');
      return {
        blob: await reportToExcelBlob(report, columns, generatedOn),
        filename: reportFilename(report, 'xlsx'),
      };
    }
    case 'pdf': {
      const { reportToPdfBlob } = await import('./exportPdf');
      return {
        blob: await reportToPdfBlob(report, columns, template, branding, generatedOn),
        filename: reportFilename(report, 'pdf'),
      };
    }
  }
}

/** Hand the blob to the browser. Revoked on the next tick — Safari needs the delay. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
